import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import UserAvatar from '../../../../components/Avatar/UserAvatar';

export default function ViewQuestionStudentsDialog({
  open,
  onClose,
  selectedQuestion,
  questionStudents,
  loadingStudents,
  variant = 'teacher',
}) {
  const isTeacher = variant === 'teacher';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>查看提交学生 - {selectedQuestion?.title}</DialogTitle>
      <DialogContent>
        {loadingStudents ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : questionStudents.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            {isTeacher ? '暂无学生提交' : '暂无注册学生'}
          </Alert>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                统计信息
              </Typography>
              <Stack direction="row" spacing={3}>
                {isTeacher ? (
                  <>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        通过人数
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {questionStudents.filter((s) => s.status === 'passed').length}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        未提交人数
                      </Typography>
                      <Typography variant="h6" color="warning.main">
                        {questionStudents.filter(
                          (s) => s.submittedAt == null || s.submittedAt === undefined
                        ).length}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" title="已提交但未通过">
                        未通过人数
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        {questionStudents.filter(
                          (s) =>
                            (s.submittedAt != null && s.submittedAt !== undefined) &&
                            s.status !== 'passed'
                        ).length}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" title="仅已提交学生计入">
                        平均分
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        {(() => {
                          const submitted = questionStudents.filter(
                            (s) =>
                              s.submittedAt != null &&
                              s.submittedAt !== undefined &&
                              (s.score != null || s.score === 0)
                          );
                          return submitted.length > 0
                            ? (
                                submitted.reduce((sum, s) => sum + (s.score ?? 0), 0) /
                                submitted.length
                              ).toFixed(1)
                            : '0.0';
                        })()}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        总人数
                      </Typography>
                      <Typography variant="h6">{questionStudents.length}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        提交人数
                      </Typography>
                      <Typography variant="h6">
                        {
                          questionStudents.filter(
                            (s) => s.submittedAt != null && s.submittedAt !== undefined
                          ).length
                        }
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        通过人数
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {questionStudents.filter((s) => s.status === 'passed').length}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" title="已提交但未通过">
                        未通过人数（已提交）
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        {questionStudents.filter(
                          (s) =>
                            s.submittedAt != null &&
                            s.submittedAt !== undefined &&
                            s.status !== 'passed'
                        ).length}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        未提交人数
                      </Typography>
                      <Typography variant="h6" color="warning.main">
                        {questionStudents.filter(
                          (s) => s.submittedAt == null || s.submittedAt === undefined
                        ).length}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        平均分
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        {(() => {
                          const submittedStudents = questionStudents.filter(
                            (s) =>
                              s.submittedAt != null &&
                              s.submittedAt !== undefined &&
                              s.score != null
                          );
                          return submittedStudents.length > 0
                            ? (
                                submittedStudents.reduce((sum, s) => sum + (s.score || 0), 0) /
                                submittedStudents.length
                              ).toFixed(1)
                            : '0.0';
                        })()}
                      </Typography>
                    </Box>
                  </>
                )}
              </Stack>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>学生姓名</strong>
                    </TableCell>
                    <TableCell>
                      <strong>手机号</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>状态</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>分数</strong>
                    </TableCell>
                    <TableCell>
                      <strong>提交时间</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {questionStudents.map((student) => {
                    const hasSubmitted =
                      student.submittedAt != null && student.submittedAt !== undefined;
                    const isPassed = student.status === 'passed';
                    const isNotSubmitted = !hasSubmitted;

                    return (
                      <TableRow key={student.userId || student.id}>
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <UserAvatar user={student} size={32} />
                            <Typography>{student.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{student.phoneNumber || '-'}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={
                              isNotSubmitted ? '未提交' : isPassed ? '通过' : '未通过'
                            }
                            size="small"
                            color={
                              isNotSubmitted
                                ? 'warning'
                                : isPassed
                                  ? 'success'
                                  : 'error'
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color={
                              isNotSubmitted
                                ? 'text.secondary'
                                : isPassed
                                  ? 'success.main'
                                  : 'error.main'
                            }
                          >
                            {student.score !== undefined && student.score !== null
                              ? student.score
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {hasSubmitted
                              ? new Date(student.submittedAt).toLocaleString('zh-CN')
                              : '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>关闭</Button>
      </DialogActions>
    </Dialog>
  );
}
